import React, { useState } from "react";
import sampleLogo from "../../assets/logo/sample1.png";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const features = [
    "Proforma Invoices & Quotations",
    "Commercial Invoices & Customs",
    "Container Packing Lists",
    "Ceramic Inventory & SKU Specs",
    "BL Draft & Shipping Advice",
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = sectionId === "hero" ? "/" : `/#${sectionId}`;
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white/95 border-b border-amber-200/60 backdrop-blur-md sticky top-0 z-50 shadow-xs text-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo & Brand Title */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection("hero")}>
            <img
              src={sampleLogo}
              alt="Export Stuff Logo"
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-8 text-xs font-bold uppercase tracking-wider text-slate-700">
            <button
              onClick={() => scrollToSection("hero")}
              className="hover:text-amber-600 transition-colors cursor-pointer"
            >
              Home
            </button>
            <div className="relative">
              <button
                onMouseEnter={() => setActiveDropdown("features")}
                onMouseLeave={() => setActiveDropdown(null)}
                className="flex items-center gap-1.5 hover:text-amber-600 transition-colors cursor-pointer"
              >
                Features
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {activeDropdown === "features" && (
                <div
                  onMouseEnter={() => setActiveDropdown("features")}
                  onMouseLeave={() => setActiveDropdown(null)}
                  className="absolute top-full left-0 w-64 bg-white border border-amber-200/80 rounded-2xl shadow-xl p-3 space-y-1"
                >
                  {features.map((f, i) => (
                    <button
                      key={i}
                      onClick={() => scrollToSection("features")}
                      className="block w-full text-left text-xs font-semibold text-slate-700 hover:text-amber-700 hover:bg-amber-50 px-3 py-2 rounded-xl transition-all cursor-pointer"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => scrollToSection("solutions")}
              className="hover:text-amber-600 transition-colors cursor-pointer"
            >
              Solutions
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="hover:text-amber-600 transition-colors cursor-pointer"
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection("faq")}
              className="hover:text-amber-600 transition-colors cursor-pointer"
            >
              FAQ
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="hover:text-amber-600 transition-colors cursor-pointer"
            >
              Contact Us
            </button>
          </div>

          {/* Action CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/login"
              className="text-xs font-bold text-slate-700 hover:text-amber-700 px-3 py-2 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-md shadow-amber-600/20 transition-all hover:scale-105 active:scale-95"
            >
              <span>Get Started Free</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-xl bg-slate-100 text-slate-700 hover:text-slate-900 border border-slate-200"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 py-4 bg-white space-y-3 px-2">
            <button
              onClick={() => scrollToSection("hero")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("features")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              Features & Modules
            </button>
            <button
              onClick={() => scrollToSection("solutions")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              Solutions
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection("faq")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              FAQ
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="block w-full text-left px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-amber-50 rounded-xl"
            >
              Contact Support
            </button>
            <div className="pt-3 border-t border-slate-200 flex flex-col gap-2">
              <Link
                to="/login"
                className="w-full text-center py-2.5 bg-slate-100 text-slate-900 font-bold text-xs rounded-xl border border-slate-200"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="w-full text-center py-2.5 bg-amber-600 text-white font-bold text-xs rounded-xl shadow-md"
              >
                Create Account
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
